import { useRef } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';

export const PayslipView = ({ payrollRecord, handleItemSelect }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const printRef = useRef<HTMLDivElement>(null);

  const employee = useLiveQuery(() => 
    payrollRecord ? db.employees.get(payrollRecord.employeeId) : null
  , [payrollRecord]);

  if (!payrollRecord) return <div className="p-4 text-gray-500">Payroll record not found.</div>;
  if (employee === undefined) return <div className="p-4 text-gray-500">Loading...</div>;
  if (employee === null) return <div className="p-4 text-red-500">Employee data nahi mila. (ID: {payrollRecord.employeeId})</div>;

  const monthName = new Date(2000, payrollRecord.month - 1).toLocaleString('default', { month: 'long' });
  
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black overflow-y-auto">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px] no-print">
        <span className="w-1/3 text-left">Payslip</span>
        <span className="w-1/3 text-center uppercase">{activeCompany?.name}</span>
        <div className="w-1/3 text-right flex justify-end gap-3">
           <span className="cursor-pointer hover:text-gray-300 underline" onClick={handlePrint}>Print</span>
           <span className="cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
        </div>
      </div>

      <div ref={printRef} className="p-8 max-w-[800px] mx-auto bg-white print:p-0">
         <div className="border-2 border-black p-4">
            {/* Header */}
            <div className="text-center border-b-2 border-black pb-4 mb-4">
               <h1 className="text-2xl font-bold uppercase">{activeCompany?.name}</h1>
               <p className="text-[14px]">{activeCompany?.address || 'Company Address'}</p>
               <p className="font-bold mt-2">Payslip for the period of {monthName} {payrollRecord.year}</p>
            </div>

            {/* Employee Info */}
            <div className="grid grid-cols-2 gap-y-2 mb-6 text-[13px]">
               <div className="flex"><span className="w-[120px] font-bold">Employee ID</span><span>: {employee.employeeId}</span></div>
               <div className="flex"><span className="w-[120px] font-bold">Name</span><span>: {employee.name}</span></div>
               <div className="flex"><span className="w-[120px] font-bold">Department</span><span>: {employee.department}</span></div>
               <div className="flex"><span className="w-[120px] font-bold">Designation</span><span>: {employee.designation}</span></div>
               <div className="flex"><span className="w-[120px] font-bold">Bank Name</span><span>: {employee.bankName}</span></div>
               <div className="flex"><span className="w-[120px] font-bold">Account No.</span><span>: {employee.bankAccountNo}</span></div>
               <div className="flex"><span className="w-[120px] font-bold">Joining Date</span><span>: {employee.joiningDate}</span></div>
               <div className="flex"><span className="w-[120px] font-bold">Status</span><span>: {employee.status}</span></div>
            </div>

            {/* Earnings & Deductions Table */}
            <div className="flex border-t-2 border-black">
               {/* Earnings */}
               <div className="flex-1 border-r-2 border-black">
                  <div className="bg-gray-100 border-b-2 border-black px-2 py-1 font-bold text-center">EARNINGS</div>
                  <div className="p-2 flex flex-col gap-1 min-h-[200px]">
                     <div className="flex justify-between"><span>Basic Salary</span><span className="font-bold">₹ {(payrollRecord.basicSalary || 0).toFixed(2)}</span></div>
                     {Array.isArray(payrollRecord.earnings) ? payrollRecord.earnings.map((e: any, i: number) => (
                       <div key={i} className="flex justify-between"><span>{e.name}</span><span className="font-bold">₹ {e.amount.toFixed(2)}</span></div>
                     )) : (
                       <>
                         <div className="flex justify-between"><span>HRA</span><span className="font-bold">₹ {payrollRecord.earnings.hra.toFixed(2)}</span></div>
                         <div className="flex justify-between"><span>Conveyance</span><span className="font-bold">₹ {payrollRecord.earnings.conveyance.toFixed(2)}</span></div>
                         <div className="flex justify-between"><span>Other Allowances</span><span className="font-bold">₹ {payrollRecord.earnings.otherAllowances.toFixed(2)}</span></div>
                       </>
                     )}
                  </div>
                  <div className="border-t-2 border-black bg-gray-50 px-2 py-1 font-bold flex justify-between">
                     <span>Total Earnings</span>
                     <span>₹ {Array.isArray(payrollRecord.earnings) ? ((payrollRecord.basicSalary || 0) + payrollRecord.earnings.reduce((s:number,e:any)=>s+e.amount,0)).toFixed(2) : (payrollRecord.earnings.basic + payrollRecord.earnings.hra + payrollRecord.earnings.conveyance + payrollRecord.earnings.otherAllowances).toFixed(2)}</span>
                  </div>
               </div>

               {/* Deductions */}
               <div className="flex-1">
                  <div className="bg-gray-100 border-b-2 border-black px-2 py-1 font-bold text-center">DEDUCTIONS</div>
                  <div className="p-2 flex flex-col gap-1 min-h-[200px]">
                     {Array.isArray(payrollRecord.deductions) ? payrollRecord.deductions.map((d: any, i: number) => (
                       <div key={i} className="flex justify-between"><span>{d.name}</span><span className="font-bold">₹ {d.amount.toFixed(2)}</span></div>
                     )) : (
                       <>
                         <div className="flex justify-between"><span>Provident Fund (PF)</span><span className="font-bold">₹ {payrollRecord.deductions.pf.toFixed(2)}</span></div>
                         <div className="flex justify-between"><span>ESI</span><span className="font-bold">₹ {payrollRecord.deductions.esi.toFixed(2)}</span></div>
                         <div className="flex justify-between"><span>TDS</span><span className="font-bold">₹ {payrollRecord.deductions.tds.toFixed(2)}</span></div>
                       </>
                     )}
                  </div>
                  <div className="border-t-2 border-black bg-gray-50 px-2 py-1 font-bold flex justify-between">
                     <span>Total Deductions</span>
                     <span>₹ {Array.isArray(payrollRecord.deductions) ? payrollRecord.deductions.reduce((s:number,d:any)=>s+d.amount,0).toFixed(2) : (payrollRecord.deductions.pf + payrollRecord.deductions.esi + payrollRecord.deductions.tds).toFixed(2)}</span>
                  </div>
               </div>
            </div>

            {/* Net Salary */}
            <div className="border-t-2 border-black p-4 flex justify-between items-center bg-gray-100">
               <div>
                  <p className="text-[14px] font-bold">Net Salary Payable</p>
                  <p className="text-[11px] italic text-gray-600">Amount in words: Rupees {numberToWords(payrollRecord.netSalary)} Only</p>
               </div>
               <div className="text-2xl font-bold">₹ {payrollRecord.netSalary.toFixed(2)}</div>
            </div>

            {/* Footer */}
            <div className="mt-12 flex justify-between px-4 pb-4">
               <div className="text-center border-t border-black pt-2 w-[150px]">Employee Signature</div>
               <div className="text-center border-t border-black pt-2 w-[150px]">Authorized Signatory</div>
            </div>
         </div>
         <div className="mt-4 text-[10px] text-gray-400 text-center">This is a computer generated payslip and does not require a physical signature.</div>
      </div>
    </div>
  );
};

function numberToWords(num: number): string {
  // Simple implementation for demo
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero';

  const n = Math.floor(num);
  if (n < 20) return a[n];
  if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
  if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + numberToWords(n % 100) : '');
  if (n < 100000) return numberToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + numberToWords(n % 1000) : '');
  
  return num.toString(); // Fallback
}